import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Bell, 
  Shield, 
  HelpCircle, 
  MessageSquare,
  AlertTriangle,
  RotateCcw,
  Eye,
  Edit,
  SlidersHorizontal,
  ShieldCheck,
  LogOut
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface HamburgerMenuProps {
  onRestoreProfile?: () => void;
  onRestoreHiddenProfile?: () => void;
  onResetDiscoveryPreferences?: () => void;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  onRestoreProfile,
  onRestoreHiddenProfile,
  onResetDiscoveryPreferences,
}) => {
  const navigate = useNavigate();
  const { isAdminOrModerator, loading: adminLoading } = useAdminRole();
  const { signOut, profile, user } = useAuthContext();
  const [open, setOpen] = useState(false);

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (profile?.name) {
      return profile.name.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const handleNavigation = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const handleAction = (action?: () => void) => {
    setOpen(false);
    action?.();
  };

  const handleSignOut = async () => {
    setOpen(false);
    try {
      const { error } = await signOut();
      if (error) {
        console.error("Sign out error:", error);
        toast({ 
          title: "Error signing out", 
          description: error.message || "Please try again",
          variant: "destructive" 
        });
      } else {
        // Navigate to entry page after successful sign out
        // The auth state change listener will handle clearing state
        navigate("/entry", { replace: true });
      }
    } catch (err) {
      console.error("Sign out exception:", err);
      toast({ 
        title: "Error signing out", 
        description: "An unexpected error occurred",
        variant: "destructive" 
      });
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="h-10 w-10 rounded-full ring-2 ring-border/50 hover:ring-primary/50 transition-all"
          aria-label="Profile menu"
        >
          <Avatar className="h-10 w-10">
            <AvatarImage 
              src={profile?.photo_url || ''} 
              alt={profile?.name || 'User'} 
              className="object-cover"
            />
            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-primary-foreground text-sm font-medium">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-56 rounded-xl bg-background/95 backdrop-blur-md border border-border/50 shadow-xl animate-in slide-in-from-top-2 duration-200"
        sideOffset={8}
      >
        {/* Profile Section */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Profile
        </DropdownMenuLabel>
        
        <DropdownMenuItem
          onClick={() => handleNavigation('/profile')}
          className="rounded-lg cursor-pointer"
        >
          <Eye className="mr-2 h-4 w-4" />
          <span>View Profile</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleNavigation('/profile?edit=true')}
          className="rounded-lg cursor-pointer"
        >
          <Edit className="mr-2 h-4 w-4" />
          <span>Edit Profile</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-border/50" />

        {/* Activity & Settings Section */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Activity & Settings
        </DropdownMenuLabel>
        
        <DropdownMenuItem 
          onClick={() => handleNavigation('/notifications')}
          className="rounded-lg cursor-pointer"
        >
          <Bell className="mr-2 h-4 w-4" />
          <span>Notifications</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleNavigation('/settings')}
          className="rounded-lg cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>Account Settings</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleNavigation('/settings?tab=privacy')}
          className="rounded-lg cursor-pointer"
        >
          <Shield className="mr-2 h-4 w-4" />
          <span>Privacy & Safety</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-border/50" />

        {/* Support Section */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Support
        </DropdownMenuLabel>
        
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="rounded-lg cursor-pointer">
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>Help Desk</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="rounded-xl bg-background/95 backdrop-blur-md border border-border/50">
            <DropdownMenuItem 
              onClick={() => handleNavigation('/help/faqs')}
              className="rounded-lg cursor-pointer"
            >
              <HelpCircle className="mr-2 h-4 w-4" />
              <span>FAQs</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleNavigation('/help/contact')}
              className="rounded-lg cursor-pointer"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span>Contact Support</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleNavigation('/help/report')}
              className="rounded-lg cursor-pointer"
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              <span>Report an Issue</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Admin Section - Only visible to admins */}
        {!adminLoading && isAdminOrModerator && (
          <>
            <DropdownMenuSeparator className="bg-border/50" />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Admin
            </DropdownMenuLabel>
            <DropdownMenuItem 
              onClick={() => handleNavigation('/admin')}
              className="rounded-lg cursor-pointer text-amber-600 dark:text-amber-400"
            >
          <ShieldCheck className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span>Admin Panel</span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator className="bg-border/50" />

        {/* Sign Out */}
        <DropdownMenuItem 
          onClick={handleSignOut}
          className="rounded-lg cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HamburgerMenu;
